import { motion, useInView } from "framer-motion";
import { useRef } from "react";
import { ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import serviceResidential from "@/assets/service-residential.webp";
import serviceCommercial from "@/assets/service-commercial.webp";
import serviceRemodel from "@/assets/service-remodel.webp";
import serviceWaterheater from "@/assets/service-waterheater.webp";

const services = [
  {
    title: "Residential Plumbing",
    description: "From leak repairs to full re-pipes, we keep your home's plumbing running smoothly.",
    image: serviceResidential,
    href: "/services/residential",
  },
  {
    title: "Water Heaters",
    description: "Tank and tankless installation, repair, and replacement — plus grinder pumps and specialty equipment.",
    image: serviceWaterheater,
    href: "/services/water-heaters",
  },
  {
    title: "Commercial Plumbing",
    description: "Dependable service for restaurants, hotels, retail, and businesses of every size.",
    image: serviceCommercial,
    href: "/services/commercial",
  },
  {
    title: "Kitchen & Bath Remodels",
    description: "Expert plumbing for your renovation — fixtures, rough-ins, and everything in between.",
    image: serviceRemodel,
    href: "/services/remodeling",
  },
];

export const Services = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <section className="py-24 bg-background relative">
      <div className="container mx-auto px-4" ref={ref}>
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          className="text-center mb-16"
        >
          <span className="text-primary font-semibold tracking-wider uppercase text-sm">
            What We Do
          </span>
          <h2 className="text-4xl md:text-5xl font-bold mt-4 mb-4">
            Our Services
          </h2>
          <p className="text-muted-foreground max-w-2xl mx-auto">
            Emergency repairs, water heaters, drain cleaning in Gulf Shores AL, remodels — whatever you need, we've got you covered as your go-to plumber across Baldwin County.
          </p>
        </motion.div>

        {/* Services Grid */}
        <div className="grid md:grid-cols-2 gap-6">
          {services.map((service, index) => (
            <motion.div
              key={service.title}
              initial={{ opacity: 0, y: 40 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: index * 0.1 }}
            >
              <Link
                to={service.href}
                className="group block relative overflow-hidden rounded-2xl bg-card border border-border hover:border-primary/50 transition-all duration-500 shadow-card hover:shadow-elevated"
              >
                {/* Image with explicit dimensions */}
                <div className="aspect-[16/10] overflow-hidden relative">
                  <img
                    src={service.image}
                    alt={`${service.title} services in Gulf Shores, Orange Beach, and Baldwin County AL - Onyx Professional Plumbing`}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                    width="640"
                    height="400"
                    loading="lazy"
                    decoding="async"
                  />
                  {/* Desktop-only subtle fade for legibility */}
                  <div className="absolute inset-0 hidden md:block bg-gradient-to-t from-background/55 via-background/10 to-transparent" />
                </div>

                {/* Content (below image on mobile, overlay on desktop) */}
                <div className="p-6 md:p-8 bg-card md:bg-transparent md:absolute md:bottom-0 md:left-0 md:right-0">
                  <div className="md:max-w-xl md:rounded-2xl md:bg-background/65 md:backdrop-blur-sm md:border md:border-border/40 md:p-6">
                    <h3 className="text-2xl font-bold mb-2 text-foreground group-hover:text-primary transition-colors">
                      {service.title}
                    </h3>
                    <p className="text-foreground/80 mb-4">
                      {service.description}
                    </p>
                    <div className="flex items-center text-primary font-medium group-hover:gap-3 gap-2 transition-all">
                      <span>Learn More</span>
                      <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
                    </div>
                  </div>
                </div>
              </Link>
            </motion.div>
          ))}
        </div>

        {/* Additional Services List */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 0.4 }}
          className="mt-12 text-center"
        >
          <p className="text-muted-foreground mb-4">
            We also handle: <Link to="/services/emergency" className="text-primary hover:underline">Emergency Repairs</Link> • <Link to="/services/drain-cleaning" className="text-primary hover:underline">Drain Cleaning</Link> • Grinder Pumps • Backflow Testing • Leak Detection • Pipe Replacement • Fixture Installation
          </p>
        </motion.div>

        {/* View All CTA */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 0.5 }}
          className="text-center mt-8"
        >
          <Button variant="hero" size="lg" asChild>
            <Link to="/services">
              View All Services
              <ArrowRight className="h-4 w-4" />
            </Link>
          </Button>
        </motion.div>
      </div>
    </section>
  );
};
